import { Download, ExternalLink, Loader2 } from 'lucide-react';

import { useGetBlobDetailQuery } from '@/services/api/blobService';

interface DocumentPreviewPanelProps {
  matterId: string;
  blobId: string;
  title: string;
}

export const DocumentPreviewPanel: React.FC<DocumentPreviewPanelProps> = ({
  matterId,
  blobId,
  title,
}) => {
  const { data: blob, isLoading, isError } = useGetBlobDetailQuery(
    { matterId, blobId },
    { skip: !matterId || !blobId },
  );

  const downloadUrl = blob?.download_url;
  const openInNewTab = () => {
    if (!downloadUrl) return;
    window.open(downloadUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="h-full flex flex-col">
      <div className="px-4 py-3 border-b border-gray-100">
        <h3 className="text-sm font-semibold text-gray-900 truncate" title={title}>
          {title}
        </h3>
        {blob?.collection_details?.name && (
          <p className="text-xs text-gray-500 mt-0.5 truncate">
            {blob.collection_details.name}
          </p>
        )}
      </div>

      <div className="px-4 py-3 flex items-center gap-2 border-b border-gray-100">
        <button
          type="button"
          onClick={openInNewTab}
          disabled={!downloadUrl}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ExternalLink className="h-3.5 w-3.5" />
          Open
        </button>
        <button
          type="button"
          onClick={openInNewTab}
          disabled={!downloadUrl}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Download className="h-3.5 w-3.5" />
          Download
        </button>
      </div>

      <div className="flex-1 bg-gray-50">
        {isLoading && (
          <div className="h-full flex items-center justify-center text-gray-500">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        )}
        {isError && (
          <div className="h-full flex items-center justify-center px-4">
            <p className="text-xs text-red-600">Failed to load document preview.</p>
          </div>
        )}
        {!isLoading && !isError && downloadUrl && (
          <iframe
            title={`Document preview - ${title}`}
            src={downloadUrl}
            className="w-full h-full border-0"
          />
        )}
        {!isLoading && !isError && !downloadUrl && (
          <div className="h-full flex items-center justify-center px-4">
            <p className="text-xs text-gray-500">Preview is unavailable for this document.</p>
          </div>
        )}
      </div>
    </div>
  );
};
